import { clsx } from '@alexis/helpers/clsx';
import { getCountryConfig, getFirstValidCountryByCallingCode } from '@alexis/helpers/country';
import { translateText } from '@alexis/helpers/translation';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import Button from '@components/Button';
import CountrySelector from '@components/CountrySelector';
import DateInput from '@components/DateInput';
import RadioGroup from '@components/flightBooking/form/RadioGroup';
import Input from '@components/Input';
import PhoneNumberInput from '@components/PhoneNumberInput';

import {
  getCurrentSiteState,
  getIsRtlState,
  getLocaleState,
  getTranslationsState,
} from '@redux/selectors';

import { Country } from '@wegoTypes/country';
import { CurrentSite } from '@wegoTypes/currentSite';

import {
  formatDateForApi,
  formatDateForDisplay,
  validateDateOfBirth,
  validateExpiryDate,
} from './helpers';
import styles from './TravellerInfoForm.module.scss';
import { TravellerInfoFormData, TravellerInfoFormSubmitData } from './types';

type FormAttributes = Omit<TravellerInfoFormData, 'gender'> & {
  callingCountry: Country;
  gender: string;
};

interface TravellerInfoFormProps {
  traveller?: Partial<TravellerInfoFormData>;
  onSubmit: (data: TravellerInfoFormSubmitData) => Promise<void>;
  isSubmitting?: boolean;
  isPrimary?: boolean;
  onCancelBtn?: () => void;
  saveBtnText?: string;
}

const TravellerInfoForm = ({
  traveller,
  onSubmit,
  isSubmitting = false,
  isPrimary = false,
  onCancelBtn,
  saveBtnText,
}: TravellerInfoFormProps) => {
  const isRtl: boolean = useSelector(getIsRtlState);

  const translations = useSelector(getTranslationsState);

  const currentSite: CurrentSite = useSelector(getCurrentSiteState);
  const locale = useSelector(getLocaleState);

  const { handleSubmit, control, watch, formState, reset } = useForm<FormAttributes>({
    mode: 'all',
    defaultValues: {
      firstName: traveller?.firstName || '',
      lastName: traveller?.lastName || '',
      email: traveller?.email || '',
      gender: traveller?.gender || '',
      nationality: traveller?.nationality || '',
      dateOfBirth: formatDateForDisplay(traveller?.dateOfBirth) || '',
      callingCountry:
        getFirstValidCountryByCallingCode(traveller?.phoneCountryCode || '') ||
        getCountryConfig(currentSite.countryCode),
      phoneNumber: traveller?.phoneNumber || '',
      passportNumber: traveller?.passportNumber || '',
      passportExpiryDate: formatDateForDisplay(traveller?.passportExpiryDate) || '',
      nationalIdNumber: traveller?.nationalIdNumber || '',
      nationalIdExpiryDate: formatDateForDisplay(traveller?.nationalIdExpiryDate) || '',
    },
  });

  // Watch passport and national ID fields for conditional validation
  const watchPassportNumber = watch('passportNumber');
  const watchPassportExpiry = watch('passportExpiryDate');
  const watchNationalIdNumber = watch('nationalIdNumber');
  const watchNationalIdExpiry = watch('nationalIdExpiryDate');

  const handleFormSubmission = (data: FormAttributes) => {
    onSubmit({
      firstName: data.firstName,
      lastName: data.lastName,
      nationality: data.nationality,
      gender: data.gender as 'MALE' | 'FEMALE',
      dateOfBirth: formatDateForApi(data.dateOfBirth),
      passportExpiryDate: formatDateForApi(data.passportExpiryDate),
      passportNumber: data.passportNumber,
      nationalIdExpiryDate: formatDateForApi(data.nationalIdExpiryDate),
      nationalIdNumber: data.nationalIdNumber,
      phoneNumber: data.phoneNumber || undefined,
      phoneCountryCode: data.phoneNumber ? `+${data.callingCountry.callingCode}` : undefined,
    });
    reset(data, {
      keepDirty: false,
      keepValues: true,
    });
  };
  return (
    <div className={styles.container}>
      <form className={styles.formWrapper} onSubmit={handleSubmit(handleFormSubmission)}>
        {/* Personal Details */}
        <div className={styles.formSection}>
          <div className={styles.sectionTitle}>{translations.personal_details}</div>
          <div className={styles.sectionNote}>
            {`${translations.all_fields_mandatory}. ${translations.enter_exact_passport_id}`}
          </div>
          <div className={styles.inputFlexContainer}>
            {/* First name */}
            <Controller
              control={control}
              name='firstName'
              rules={{
                required: `${translations.this_field_is_required}`,
                pattern: {
                  value: /^(?=.{2,30}$)[a-zA-Z]+(\s[a-zA-Z]+)*$/,
                  message: translateText(
                    translations.invalid,
                    locale,
                    translations.first_name as string,
                  ),
                },
              }}
              render={({ field, fieldState }) => (
                <Input
                  autoComplete='first-name'
                  hasError={!!fieldState.error?.message}
                  isRtl={isRtl}
                  className={styles.fullWidth}
                  id='firstName'
                  isValidated={!!fieldState.error?.message || !!field.value}
                  message={fieldState.error?.message}
                  onChange={field.onChange}
                  placeholder={translations.first_name}
                  type='text'
                  value={field.value}
                />
              )}
            />
            {/* Last name */}
            <Controller
              control={control}
              name='lastName'
              rules={{
                required: `${translations.this_field_is_required}`,
                pattern: {
                  value: /^(?=.{2,30}$)[a-zA-Z]+(\s[a-zA-Z]+)*$/,
                  message: translateText(
                    translations.invalid,
                    locale,
                    translations.autofill_lastname as string,
                  ),
                },
              }}
              render={({ field, fieldState }) => (
                <Input
                  autoComplete='last-name'
                  hasError={!!fieldState.error?.message}
                  isRtl={isRtl}
                  className={styles.fullWidth}
                  id='lastName'
                  isValidated={!!fieldState.error?.message || !!field.value}
                  message={fieldState.error?.message}
                  onChange={field.onChange}
                  placeholder={translations.last_name}
                  type='text'
                  value={field.value}
                />
              )}
            />
          </div>
          <div className={styles.genderInputContainer}>
            <div className={styles.inputTitle}>{translations.gender_label}</div>
            <div className={styles.inputFlexContainer}>
              <Controller
                control={control}
                name='gender'
                rules={{
                  required: `${translations.this_field_is_required}`,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <RadioGroup
                      onChange={field.onChange}
                      options={[
                        {
                          label: translations.male,
                          value: 'MALE',
                        },
                        {
                          label: translations.female,
                          value: 'FEMALE',
                        },
                      ]}
                      value={field.value}
                      hasError={!!fieldState.error}
                    />
                  );
                }}
              />
            </div>
          </div>
          <div className={styles.inputFlexContainer}>
            {/* Nationality */}
            <Controller
              control={control}
              name='nationality'
              rules={{
                required: `${translations.this_field_is_required}`,
              }}
              render={({ field, fieldState }) => {
                return (
                  <CountrySelector
                    className={styles.fullWidth}
                    currentSite={currentSite}
                    hasError={!!fieldState.error?.message}
                    id='nationality'
                    isRtl={isRtl}
                    isValidated={!!fieldState.error?.message}
                    message={fieldState.error?.message}
                    onCountrySelected={(country) => {
                      field.onChange(country.code);
                    }}
                    placeholder={`${translations.autofill_passport_nationality}`}
                    selectedCountry={getCountryConfig(field.value)}
                    translations={translations}
                  />
                );
              }}
            />
            {/* Date of birth */}
            <Controller
              control={control}
              name='dateOfBirth'
              rules={{
                required: `${translations.this_field_is_required}`,
                validate: (value: string) =>
                  validateDateOfBirth(value, isPrimary, translations, locale),
              }}
              render={({ field, fieldState }) => {
                return (
                  <DateInput
                    autoComplete='date-of-birth'
                    className={clsx(styles.fullWidth, styles.dateInput)}
                    dateFormat='DD/MM/YYYY'
                    hasError={!!fieldState.error?.message}
                    id='dateOfBirth'
                    isRtl={isRtl}
                    isValidated={!!fieldState.error?.message || !!field.value}
                    message={fieldState.error?.message}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    placeholder={`${translations.date_of_birth}`}
                    value={field.value}
                  />
                );
              }}
            />
          </div>
        </div>
        <div className={styles.divider}></div>
        {/* Contact Information */}
        {isPrimary && (
          <>
            <div className={styles.formSection}>
              <div className={styles.contactInfoContainer}>
                <div className={styles.sectionTitle}>{translations.contact_info}</div>
                <div className={styles.sectionNote}>{translations.filling_contact_information}</div>

                <div className={styles.inputFlexContainer}>
                  {/* Email */}
                  <Controller
                    control={control}
                    name='email'
                    rules={{
                      pattern: {
                        value: /^\w+([\.+-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                        message: translateText(
                          translations.em_invalid,
                          locale,
                          translations.helpers_label_user_email as string,
                        ),
                      },
                    }}
                    render={({ field, fieldState }) => {
                      return (
                        <Input
                          autoComplete='email'
                          className={styles.fullWidth}
                          hasError={!!fieldState.error?.message}
                          isRtl={isRtl}
                          isValidated={!!fieldState.error?.message || !!field.value}
                          message={fieldState.error?.message}
                          onChange={field.onChange}
                          placeholder={`${translations.email_addr}`}
                          type='email'
                          value={field.value}
                          disabled={true}
                        />
                      );
                    }}
                  />
                  {/* Phone Number */}
                  <div className={styles.fullWidth}>
                    <div className={styles.inputFlexContainer}>
                      <Controller
                        name='callingCountry'
                        control={control}
                        render={({ field: countryField }) => (
                          <Controller
                            name='phoneNumber'
                            control={control}
                            rules={{
                              pattern: {
                                value: /^\d{4,13}$/,
                                message: translateText(
                                  translations.phone_number_length_helper,
                                  locale,
                                  4,
                                  13,
                                ),
                              },
                            }}
                            render={({ field, fieldState }) => (
                              <PhoneNumberInput
                                currentSite={currentSite}
                                isPhoneNumberValidated={
                                  !!fieldState.error?.message || !!field.value
                                }
                                isRtl={isRtl}
                                message={fieldState.error?.message as string}
                                onCountrySelected={countryField.onChange}
                                onPhoneNumberBlur={field.onBlur}
                                onPhoneNumberChange={field.onChange}
                                phoneNumberhasError={!!fieldState.error?.message}
                                placeholder={translations.phone_number as string}
                                selectedCountry={countryField.value as Country}
                                translations={translations}
                                value={field.value}
                                hideSelectedCountryFlag={true}
                                callingCodeLabel={translations.country_code as string}
                              />
                            )}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.divider}></div>
          </>
        )}
        {/* Travel Documents */}
        <div className={styles.formSection}>
          <div>
            <div className={styles.sectionTitle}>{translations.travel_documents}</div>
            <div className={styles.sectionNote}>
              {`${translations.id_validity_warning}. (${translations.optional})`}
            </div>
            {/* Passport */}
            <div className={styles.travelFieldTitle}>{translations.passport}</div>
            <div className={styles.inputFlexContainer}>
              {/* Passport Number */}
              <Controller
                control={control}
                name='passportNumber'
                rules={{
                  required: watchPassportExpiry && `${translations.this_field_is_required}`,
                  pattern: {
                    value: /^[A-Z0-9]{2,30}$/i,
                    message: translateText(
                      translations.invalid,
                      locale,
                      translations.passport_no as string,
                    ),
                  },
                }}
                render={({ field, fieldState }) => (
                  <Input
                    autoComplete='passport-number'
                    hasError={!!fieldState.error?.message}
                    isRtl={isRtl}
                    className={styles.fullWidth}
                    id='passportNumber'
                    isValidated={!!fieldState.error?.message || !!field.value}
                    message={fieldState.error?.message}
                    onChange={(value) => field.onChange(value.toUpperCase())}
                    placeholder={`${translations.passport_no}`}
                    type='text'
                    value={field.value}
                  />
                )}
              />
              {/* Passport Date of Expiry */}
              <Controller
                control={control}
                name='passportExpiryDate'
                rules={{
                  required: watchPassportNumber && `${translations.this_field_is_required}`,
                  validate: (value: string) => validateExpiryDate(value, translations, locale),
                }}
                render={({ field, fieldState }) => {
                  return (
                    <DateInput
                      autoComplete='passport-exp'
                      className={clsx(styles.fullWidth, styles.dateInput)}
                      dateFormat='DD/MM/YYYY'
                      hasError={!!fieldState.error?.message}
                      id='passportExpiryDate'
                      isRtl={isRtl}
                      isValidated={!!fieldState.error?.message || !!field.value}
                      message={fieldState.error?.message}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      placeholder={`${translations.date_of_expiry}`}
                      value={field.value}
                    />
                  );
                }}
              />
            </div>

            {/* National ID */}
            <div className={styles.travelFieldTitle}>{translations.national_id}</div>
            <div className={styles.inputFlexContainer}>
              {/* ID Card Number */}
              <Controller
                control={control}
                name='nationalIdNumber'
                rules={{
                  required: watchNationalIdExpiry && `${translations.this_field_is_required}`,
                  pattern: {
                    value: /^[A-Z0-9]{2,30}$/i,
                    message: translateText(
                      translations.invalid,
                      locale,
                      translations.id_card_no as string,
                    ),
                  },
                }}
                render={({ field, fieldState }) => (
                  <Input
                    autoComplete='id'
                    hasError={!!fieldState.error?.message}
                    isRtl={isRtl}
                    className={styles.fullWidth}
                    id='nationalIdNumber'
                    isValidated={!!fieldState.error?.message || !!field.value}
                    message={fieldState.error?.message}
                    onChange={(value) => field.onChange(value.toUpperCase())}
                    placeholder={translations.id_card_no as string}
                    type='text'
                    value={field.value}
                  />
                )}
              />
              {/* National ID Date of Expiry */}
              <Controller
                control={control}
                name='nationalIdExpiryDate'
                rules={{
                  required: watchNationalIdNumber && `${translations.this_field_is_required}`,
                  validate: (value: string) => validateExpiryDate(value, translations, locale),
                }}
                render={({ field, fieldState }) => {
                  return (
                    <DateInput
                      autoComplete='id-exp'
                      className={clsx(styles.fullWidth, styles.dateInput)}
                      dateFormat='DD/MM/YYYY'
                      hasError={!!fieldState.error?.message}
                      id='nationalIdExpiryDate'
                      isRtl={isRtl}
                      isValidated={!!fieldState.error?.message || !!field.value}
                      message={fieldState.error?.message}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      placeholder={`${translations.date_of_expiry}`}
                      value={field.value}
                    />
                  );
                }}
              />
            </div>
          </div>
        </div>
        <div className={styles.actionContainer}>
          {onCancelBtn && (
            <Button
              onClick={onCancelBtn}
              type='button'
              variant='outline'
              colorScheme='grey'
              className={styles.actionBtn}
            >
              {translations.cancel}
            </Button>
          )}
          <Button
            type='submit'
            aria-label='Form Submit Button'
            className={styles.actionBtn}
            isLoading={isSubmitting}
            disabled={!formState.isDirty}
          >
            {saveBtnText || translations.save}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TravellerInfoForm;
